// Blocking is measured over every row, hang lines included, but a request names shelves only:
// the pogger keeps each SKU on its own shelf type, so a peg-row restriction is dropped, not moved.
import type { StratumPlan } from "./bayPartition";
import type { ReplicaRowHome } from "./finalize";
import type {
  GeneratedLayout,
  StudioItem,
  StudioPlacement,
  StudioRunGeometry,
} from "./studioTypes";

// Pegboard products are immutable in a Studio layout: generators and moves see the shelves only,
// row ranks unchanged, so no layout claims a hanging product moved and the preview leaves it hung.
export const shelfLayoutItemsOf = (items: readonly StudioItem[]): readonly StudioItem[] => {
  if (!items.some((item) => item.placements.some((placement) => placement.pegboard))) return items;
  return items.flatMap((item) => {
    const placements = item.placements.filter((placement) => !placement.pegboard);
    if (placements.length === item.placements.length) return [item];
    // Facings are what the shelves hold, or the pack would lay the hung facings out too.
    const facings = placements.reduce((sum, placement) => sum + placement.hFacings, 0);
    return placements.length > 0 ? [{ ...item, facings, placements }] : [];
  });
};

export const shelfLayoutRunsOf = (
  runs: readonly StudioRunGeometry[],
): readonly StudioRunGeometry[] =>
  runs.some((run) => run.shelves.some((row) => row.pegboard))
    ? runs.map((run) => ({ ...run, shelves: run.shelves.filter((row) => !row.pegboard) }))
    : runs;

/** The rows with every empty hang line taken out, ranks renumbered in order. A board has a hole
 *  row every inch or so: two hang lines with only unused holes between them are adjacent rows,
 *  and an unused hole row is board surface, not a row a value failed to fill. */
export interface CompactRows {
  /** False when no hang line is empty; every member is then the identity. */
  readonly active: boolean;
  readonly runs: readonly StudioRunGeometry[];
  readonly items: readonly StudioItem[];
  /** Row rank -> compact rank; a rank it drops keeps its own number. */
  ordinalOf: (rank: number) => number;
  /** Compact rank -> the row rank it stands for. */
  rankOf: (ordinal: number) => number;
}

export const compactRowsOf = (
  runs: readonly StudioRunGeometry[],
  items: readonly StudioItem[],
): CompactRows => {
  // One numbering for every run, as layouts use: a rank is kept when any run uses it.
  const kept = new Set<number>();
  for (const run of runs) for (const row of run.shelves) if (!row.pegboard) kept.add(row.rank);
  for (const item of items) {
    for (const placement of item.placements) if (placement.pegboard) kept.add(placement.shelf);
  }
  const all = new Set(runs.flatMap((run) => run.shelves.map((row) => row.rank)));
  if ([...all].every((rank) => kept.has(rank))) {
    const same = (rank: number) => rank;
    return { active: false, runs, items, ordinalOf: same, rankOf: same };
  }
  const ordered = [...kept].sort((a, b) => a - b);
  const ordinal = new Map(ordered.map((rank, index) => [rank, index + 1]));
  const ordinalOf = (rank: number) => ordinal.get(rank) ?? rank;
  return {
    active: true,
    runs: runs.map((run) => ({
      ...run,
      shelves: run.shelves.flatMap((row) =>
        ordinal.has(row.rank) ? [{ ...row, rank: ordinalOf(row.rank) }] : [],
      ),
    })),
    items: items.map((item) => ({
      ...item,
      placements: item.placements.map((placement) => ({
        ...placement,
        shelf: ordinalOf(placement.shelf),
      })),
    })),
    ordinalOf,
    rankOf: (compact) => ordered[compact - 1] ?? compact,
  };
};

/** An item's placements under a layout, its pegboard placements carried through as read. */
export const layoutPlacementsOf = (
  item: StudioItem,
  layout: GeneratedLayout,
): StudioPlacement[] => [
  ...(layout.placements[item.skuId]?.segments ?? []).map((segment) => ({
    run: segment.run,
    shelf: segment.shelf,
    xCm: segment.xCm,
    widthCm: segment.hFacings * item.facingWidthCm,
    hFacings: segment.hFacings,
  })),
  ...item.placements.filter((placement) => placement.pegboard),
];

/** Studio row rank -> shelf rank, or null for a hang line. Ranks it does not list pass through. */
export const shelfRankMapOf = (
  run: StudioRunGeometry | undefined,
): ReadonlyMap<number, number | null> => {
  const map = new Map<number, number | null>();
  let shelfRank = 0;
  for (const row of [...(run?.shelves ?? [])].sort((a, b) => a.rank - b.rank)) {
    if (map.has(row.rank)) continue;
    map.set(row.rank, row.pegboard ? null : (shelfRank += 1));
  }
  return map;
};

const translate = (map: ReadonlyMap<number, number | null>, rank: number): number | null => {
  const shelf = map.get(rank);
  return shelf === undefined ? rank : shelf;
};

/** The run's shelf ranks in shelf-only space, ascending. */
export const shelfRanksOf = (run: StudioRunGeometry | undefined): number[] =>
  [...new Set(shelfRankMapOf(run).values())]
    .filter((rank): rank is number => rank !== null)
    .sort((a, b) => a - b);

/** A placement's rank in shelf-only space; null when it hangs on a pegboard. */
export const shelfRankOfPlacement = (
  placement: StudioItem["placements"][number],
  run: StudioRunGeometry | undefined,
): number | null => (placement.pegboard ? null : translate(shelfRankMapOf(run), placement.shelf));

export interface ShelfRankSpace {
  /** False when no run has a hang line; every translation is then the identity. */
  readonly active: boolean;
  readonly runs: readonly StudioRunGeometry[];
  /** Shelf ranks of the first run, the wall every fence is drawn on. */
  readonly shelfRanks: readonly number[];
  items: (items: readonly StudioItem[]) => readonly StudioItem[];
  ranks: (ranks: readonly number[]) => number[];
  stratum: (stratum: StratumPlan) => StratumPlan;
  rowHomes: (rowHomes: readonly ReplicaRowHome[]) => ReplicaRowHome[];
}

export const shelfRankSpaceOf = (runs: readonly StudioRunGeometry[]): ShelfRankSpace => {
  const active = runs.some((run) => run.shelves.some((row) => row.pegboard));
  const firstMap = shelfRankMapOf(runs[0]);
  const shelfRanks = shelfRanksOf(runs[0]);
  if (!active) {
    return {
      active,
      runs,
      shelfRanks,
      items: (items) => items,
      ranks: (ranks) => [...ranks],
      stratum: (stratum) => stratum,
      rowHomes: (rowHomes) => [...rowHomes],
    };
  }
  const mapOfRun = new Map(runs.map((run) => [run.run, shelfRankMapOf(run)]));
  const ranks = (list: readonly number[]): number[] =>
    list.flatMap((rank) => {
      const shelf = translate(firstMap, rank);
      return shelf === null ? [] : [shelf];
    });
  return {
    active,
    runs: runs.map((run) => {
      const map = mapOfRun.get(run.run) ?? firstMap;
      return {
        ...run,
        shelves: run.shelves.flatMap((row) => {
          const rank = translate(map, row.rank);
          return rank === null || row.pegboard
            ? []
            : [{ rank, yCm: row.yCm, thicknessCm: row.thicknessCm }];
        }),
      };
    }),
    shelfRanks,
    items: (items) =>
      items.map((item) => ({
        ...item,
        placements: item.placements.flatMap((placement) => {
          if (placement.pegboard) return [];
          const shelf = translate(mapOfRun.get(placement.run) ?? firstMap, placement.shelf);
          return shelf === null ? [] : [{ ...placement, shelf }];
        }),
      })),
    ranks,
    stratum: (stratum) => {
      const homeShelves = ranks(stratum.homeShelves);
      // A home made only of hang lines fences nothing a shelf can hold.
      return homeShelves.length > 0
        ? { ...stratum, homeShelves }
        : { ...stratum, homeShelves: [...shelfRanks], fullHeight: true, anchors: false };
    },
    rowHomes: (rowHomes) =>
      rowHomes.flatMap((rowHome) => {
        const shelves = ranks(rowHome.shelves);
        return shelves.length > 0 ? [{ ...rowHome, shelves }] : [];
      }),
  };
};
